import React, { useCallback, useMemo, useState } from 'react';
import { Box, List, PaginationBar, RawButton, SearchInput, SelectDropdown, Text } from '@common-fe/common-fe';

import { DefaultSortTypesEnum } from '@/common/types';
import { useCurrentOrganization } from '@/modules/core/hooks';

import { useRolesListQuery } from '../queries';
import { RoleLevel } from '../role.types';

import { useRolesListParams } from './hooks';
import RoleCopyModal from './RoleCopyModal';
import RolesLevelDropdown from './RolesLevelsDropdown';
import RolesListItem from './RolesListItem';
import RolesPermissionDropdown from './RolesPermissionDropdown';
import RoleSummary from './RoleSummary';

const SORTING_OPTIONS = [
  {
    key: DefaultSortTypesEnum.ASC,
    value: DefaultSortTypesEnum.ASC,
    title: 'A-Z',
  },
  {
    key: DefaultSortTypesEnum.DESC,
    value: DefaultSortTypesEnum.DESC,
    title: 'Z-A',
  },
];

const RolesList = () => {
  const [summaryRoleId, setSummaryRoleId] = useState('');
  const [copyModalRoleId, setCopyModalRoleId] = useState('');
  const [clearId, setClearId] = useState('');
  const {
    page,
    setPerPage,
    perPage,
    setCurrentPermissions,

    currentLevel,
    setCurrentLevel,
    setCurrentPartners,
    currentPartners,
    currentPermissions,
    searchString,
    setSearchString,
    setPage,
    sortBy,
    setSortBy,
  } = useRolesListParams();

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setCurrentPermissions([]);
    setCurrentLevel(RoleLevel.All);
    setCurrentPartners([]);
    setSortBy(DefaultSortTypesEnum.ASC);
    
    setSearchString('');
    setClearId(`${+new Date()}`);
    
  }, [setCurrentLevel, setCurrentPartners, setCurrentPermissions, setPage, setSearchString, setSortBy]);
  const isShowedResetButton = useMemo(() => {
    return (
      page !== 1 ||
      !!currentPermissions.length ||
      !!currentPartners.length ||
      sortBy !== DefaultSortTypesEnum.ASC ||
      !!searchString.length ||
      currentLevel !== RoleLevel.All
    );
  }, [currentLevel, currentPartners.length, currentPermissions.length, page, searchString.length, sortBy]);

  const { isLoading, total, data } = useRolesListQuery({
    permissions: currentPermissions,
    partnerIds: currentPartners,
    levelType: currentLevel,
    sortBy: sortBy as DefaultSortTypesEnum,
    page,
    perPage,
    searchString,
  });
  const { isElevateOrganization, observingMode } = useCurrentOrganization();

  return (
    <Box
      data-testid="roles-list"
      direction="column"
      pad="l"
      background="module"
      round="moduleRound"
      style={{ position: 'relative' }}
    >
      <Box
        align="center"
        style={{
          position: 'absolute',
          bottom: '100%',
          left: 0,
          right: 0,
        }}
      >
        {isShowedResetButton && (
          <RawButton onClick={handleResetFilters}>
            <Box
              round={{ corner: 'top', size: 'button2Round' }}
              background="module"
              pad={{ horizontal: '8px', vertical: '2px' }}
            >
              <Text color="textBody" weight={400}>
                Clear All
              </Text>
            </Box>
          </RawButton>
        )}
        
      </Box>
      <RoleSummary id={summaryRoleId} onChangeId={setSummaryRoleId} onCopyModalOpen={setCopyModalRoleId} />
      <RoleCopyModal id={copyModalRoleId} onChangeId={setCopyModalRoleId} />

      <Box key={clearId} direction="row" justify="between" align="center" margin={{ bottom: 'm' }}>
        <Box>
          <Text weight="bold">Roles: {total || 0}</Text>
        </Box>
        <Box direction="row">
          <Box width="240px" margin={{ right: 's' }}>
            <SearchInput
              data-testid="roles-list_search"
              placeholder="Search"
              hasSearchingHistory
              testId="roles-search"
              value={searchString}
              onChange={setSearchString}
            />
          </Box>
          <Box width="240px" margin={{ right: 's' }}>
            <RolesLevelDropdown
              level={currentLevel}
              onSetLevel={setCurrentLevel}
              partners={currentPartners}
              onSelectPartners={setCurrentPartners}
            />
          </Box>
          <Box width="240px" margin={{ right: 's' }}>
            <RolesPermissionDropdown values={currentPermissions} onChangeValues={setCurrentPermissions} />
          </Box>
          <Box width={{ min: 'control' }}>
            <SelectDropdown
              id="sort_by"
              testId='sort_roles'
              disabled={false}
              name="Sort by"
              prefix="Sort by"
              singleMode
              value={sortBy}
              onChange={setSortBy}
              activeTitle
              options={SORTING_OPTIONS}
            />
          </Box>
        </Box>
      </Box>
      <List>
        <List.Header>
          <List.Header.Item flex={1}>Name/Description</List.Header.Item>
          <List.Header.Item flex={3}>
            {isElevateOrganization && !observingMode ? 'Level/Assigned to' : 'Level'}
          </List.Header.Item>
          <Box width="24px"></Box>
        </List.Header>
        {isLoading ? (
          <List.Preloader count={10} />
        ) : (
          data.map((item) => (
            <RolesListItem 
              key={item.id}
              {...item}
              onSetCopyModal={setCopyModalRoleId}
              onOpenSummary={setSummaryRoleId} />
          ))
        )}
        {!isLoading && data.length === 0 ? <List.Placeholder /> : null}

        <PaginationBar
          page={page}
          total={total || 0}
          pageSize={perPage}
          onChangePage={setPage}
          onChangePageSize={setPerPage}
        />
      </List>
    </Box>
  );
};
export default RolesList;
